/*
Input parameters: 
- (x, y) input c and y based on selected type cordination.
- x/ytype:
-   'axes': x/y is set via the axes of the chart. (y: min is bottom)
-   'percent': x/y is scaled in min max of the area so that 0 is left/bottom and 100 is right/bottom. (y: 0 is bottom)
-   'pixel': x/y is raw chart pixels. (y: 0 is top)
-   '-pixel': x/y is raw chart pixel for the opposite side pixels so that 0 is right/bottom.
-   'dataIndex': x/y is the index of the data point.
-   'dataValue': x/y is the x/y-value of the data points rounded to the nearest value.
- x/yoffset: offset in pixels from the x/y position.

* their combination is allowed. For example, x is set via input x and y via dataIndexY. 

Example:

import React from "react";
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2'
import getChartSVGPlugin from "../../tools/chartFunctions";
ChartJS.register(...registerables);

const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Free 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free (Icons: CC BY 4.0, Fonts: SIL OFL 1.1, Code: MIT License) Copyright 2023 Fonticons, Inc. --><path d="M399 384.2C376.9 345.8 335.4 320 288 320H224c-47.4 0-88.9 25.8-111 64.2c35.2 39.2 86.2 63.8 143 63.8s107.8-24.7 143-63.8zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 16a72 72 0 1 0 0-144 72 72 0 1 0 0 144z"/></svg>`;
const updateGaugeChartText = getChartSVGPlugin('currentChartValue', svgString, 30, 30, 30, 30, 'pixel', 'pixel');
const updateGaugeChartText = getChartSVGPlugin('currentChartValue', svgString, 30, 30, 1.5, 1, 'axes', 'axes');
const updateGaugeChartText = getChartSVGPlugin('currentChartValue', svgString, 30, 30, 50, 75, 'percent', 'percent');
const updateGaugeChartText = getChartSVGPlugin('currentChartValue', svgString, 30, 30, 3, 3, 'dataIndex', 'dataIndex');
*/
export default function getChartTextPlugin(id, text,
                            x=null, y=null,  // case 1
                            xtype = 'pixel', ytype='pixel',
                            xoffset=0, yoffset=0,
                            onDrawHandler=null,
                            textFont = "bold 11px system-ui",
                            textColor = "black",
                            textDegree = 0,
                            blackBackgroundColor = 'white') {
    let res = {
        id: id,
        afterDatasetDraw(chart, args, pluginOptions) {
            const { ctx } = chart;
            let [xCoor, yCoor] = findXoordination(chart, x, y, xtype, ytype, xoffset, yoffset);
            ctx.save();
            ctx.textAlign = "center";
            ctx.fillStyle = textColor;
            ctx.font = textFont;

            ctx.translate(xCoor, yCoor);
            ctx.rotate(-textDegree * Math.PI/180);
            ctx.fillText(text, 0, 0);
            

            ctx.restore();
            if(onDrawHandler) {
                onDrawHandler(chart, args, null);
            }
        }};

    return res;
}
export function getChartSVGPlugin(id, svgString, imageWidth, imageHeight, 
                            x=null, y=null,  // case 1
                            xtype = 'pixel', ytype='pixel',
                            xoffset=0, yoffset=0,
                            onDrawHandler=null) {
    let res = {
        id: id,
        afterRender(chart, args, pluginOptions) {
            const blob = new Blob([svgString], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            const img = new Image();
            //img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAA1BMVEX/AAAZ4gk3AAAASElEQVR4nO3BgQAAAADDoPlTX+AIVQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADwDcaiAAFXD1ujAAAAAElFTkSuQmCC';
            img.src = url;
            img.onload = () => {
                const { ctx } = chart;
                let [xCoor, yCoor] = findXoordination(chart, x, y, xtype, ytype, xoffset, yoffset);

                
                const imageXPos = xCoor - imageWidth / 2;
                const imageYPos = yCoor - imageHeight / 2;
                ctx.save();
                ctx.drawImage(img, imageXPos, imageYPos, imageWidth, imageHeight);
                ctx.restore();
            };  
            if(onDrawHandler) {
                onDrawHandler(chart, args, pluginOptions);
            }
        }};

    return res;
}

function findXoordination(chart, x=null, y=null,  
                            xtype = 'pixel', ytype='pixel',
                            xoffset=0, yoffset=0) {
    const meta = chart.getDatasetMeta(0);
    var xCoor;
    var yCoor;
    if (xtype.toLowerCase() === 'pixel') {
        xCoor = x;
    } else if (xtype.toLowerCase() === '-pixel') {
        xCoor = chart.chartArea.right - x;
    } else if (xtype.toLowerCase() === 'axes') {
        xCoor = convertDataToChartCoordinateX(chart, x);
    } else if (xtype.toLowerCase() === 'percent') {
        const width = chart.chartArea.right - chart.chartArea.left;// - imageWidth;
        xCoor = x/100 * width + chart.chartArea.left;// + imageWidth/2;
    } else if (xtype.toLowerCase() === 'dataindex') {
        xCoor = meta.data[x].x;
    } else if (xtype.toLowerCase() === 'datavalue') {
        let err = chart.config.data.labels.map((e) => (Math.abs(e-x,2)));
        let roundedIndex = err.indexOf(Math.min(...err));
        xCoor = meta.data[roundedIndex].x;
    } 

    if (ytype.toLowerCase() === 'pixel') {
        yCoor = y;
    } else if (ytype.toLowerCase() === '-pixel') {
        yCoor = chart.chartArea.bottom - y;
    } else if (ytype.toLowerCase() === 'axes') {
        yCoor = convertDataToChartCoordinateY(chart, y);
    } else if (ytype.toLowerCase() === 'percent') {
        const height = chart.chartArea.bottom - chart.chartArea.top;
        yCoor = chart.chartArea.bottom - y/100 * height;
    } else if (ytype.toLowerCase() === 'dataindex') {
        yCoor = meta.data[y].y;
    } else if (ytype.toLowerCase() === 'datavalue') {
        let err = chart.config.data.datasets[0].data.map((e) => (Math.abs(e-y,2)));
        let roundedIndex = err.indexOf(Math.min(...err));
        yCoor = meta.data[roundedIndex].y;
    }

    xCoor += xoffset;
    yCoor += yoffset;

    return [xCoor, yCoor];
}
function convertDataToChartCoordinateX(chart,dataX){
    const max = chart.scales.x.max;
    const min = chart.scales.x.min;
    const width = chart.chartArea.right - chart.chartArea.left;
    const chartX = chart.chartArea.left + (dataX - min) * width  / (max - min);
    return chartX;
}
function convertDataToChartCoordinateY(chart,dataY){
    const max = chart.scales.y.max;
    const min = chart.scales.y.min;
    const height = chart.chartArea.bottom - chart.chartArea.top;
    const chartY = chart.chartArea.bottom - (dataY - min) * height / (max - min);
    return chartY;
}
function convertChartToDataCoordinateX(chart,chartX){
    const max = chart.scales.x.max;
    const min = chart.scales.x.min;
    const width = chart.chartArea.right - chart.chartArea.left;
    const dataX = (chartX - chart.chartArea.left) * (max - min) / width + min;
    return dataX;
}
function convertChartToDataCoordinateY(chart,chartY){
    const max = chart.scales.y.max;
    const min = chart.scales.y.min;
    const height = chart.chartArea.bottom - chart.chartArea.top;
    const dataY = (chart.chartArea.bottom - chartY) * (max - min) / height + min;
    return dataY;
}
